import { Crop } from 'react-image-crop';
import { ImageTransform, CropParams, TransformMatrix } from './interface';

// ==================== 工具函数 ====================

/**
 * 检查两个数字的差值是否在指定范围内
 */
export const isDifferenceInRange = (num1: number, num2: number, range: number, fixedCount = 2): boolean => {
    const difference = Math.abs(num1 - num2).toFixed(fixedCount);
    return Number(difference) <= range;
};

/**
 * 计算变换矩阵
 */
export const calculateTransformMatrix = (rotation: number, scaleX: number, scaleY: number): TransformMatrix => {
    const rad = (rotation * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);

    return {
        a: cos * scaleX,
        b: sin * scaleX,
        c: -sin * scaleY,
        d: cos * scaleY,
    };
};

/**
 * 应用变换到裁剪坐标
 */
export const applyTransformToCrop = (crop: Crop, transformMatrix: TransformMatrix) => {
    if (!crop || !crop.x || !crop.y || !crop.width || !crop.height) {
        return crop;
    }

    const { a, b, c, d } = transformMatrix;
    const centerX = crop.x + crop.width / 2;
    const centerY = crop.y + crop.height / 2;

    // 计算变换后的坐标
    const newX = centerX * a + centerY * c;
    const newY = centerX * b + centerY * d;

    return {
        ...crop,
        x: newX - crop.width / 2,
        y: newY - crop.height / 2,
    };
};

/**
 * 计算变换后的像素比
 */
export const calculatePixelRatio = (basePixelRatio: number, transformScale: number, maxRatio: number = 4): number => {
    return Math.min(basePixelRatio * transformScale, maxRatio);
};

/**
 * 检查图片是否为原图（满足裁剪比例要求）
 */
export const checkIsOriginalImage = (
    imageWidth: number,
    imageHeight: number,
    cropRatio: number,
    tolerance: number = 0.01,
): boolean => {
    const imageRatio = Number((imageWidth / imageHeight).toFixed(2));
    const cropRatioTmp = Number(cropRatio.toFixed(2));
    return isDifferenceInRange(imageRatio, cropRatioTmp, tolerance);
};

/**
 * 检查是否可以使用原图（向后兼容逻辑）
 */
export const checkLegacyOriginalImage = (
    image: HTMLImageElement,
    completedCropWidth: number,
    cropRatio: number,
): boolean => {
    const imageRatio = Number((image.width / image.height).toFixed(2));
    const cropRatioTmp = Number(cropRatio.toFixed(2));
    return (
        isDifferenceInRange(imageRatio, cropRatioTmp, 0.01) && isDifferenceInRange(completedCropWidth, image.width, 5)
    );
};

/**
 * 设置Canvas上下文
 */
export const setupCanvasContext = (canvas: HTMLCanvasElement, pixelRatio: number): CanvasRenderingContext2D => {
    const ctx = canvas.getContext('2d')!;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    return ctx;
};

/**
 * 计算裁剪参数
 */
export const calculateCropParams = (
    image: HTMLImageElement,
    completedCrop: Crop,
    cropRatio: number,
    imageTransform: ImageTransform,
): CropParams => {
    // 获取图片的显示尺寸和自然尺寸
    const displayWidth = image.width;
    const displayHeight = image.height;
    const naturalWidth = image.naturalWidth;
    const naturalHeight = image.naturalHeight;

    // 计算基础缩放比例（从显示尺寸到自然尺寸）
    const baseScaleX = naturalWidth / displayWidth;
    const baseScaleY = naturalHeight / displayHeight;

    // 获取裁剪区域的尺寸
    const completedCropWidth = completedCrop?.width || 200;
    const completedCropHeight = completedCrop?.height || completedCropWidth / cropRatio;

    // 考虑图片变换的影响
    const scaleX = imageTransform.scaleX || 1;
    const scaleY = imageTransform.scaleY || 1;
    const rotation = imageTransform.rotation || 0;

    // 基于中心点缩放的坐标转换
    // 1. 计算缩放后的图片尺寸
    const scaledWidth = displayWidth * scaleX;
    const scaledHeight = displayHeight * scaleY;

    // 2. 计算缩放中心偏移（由于缩放是基于中心点，图片左上角会偏移）
    const centerOffsetX = (scaledWidth - displayWidth) / 2;
    const centerOffsetY = (scaledHeight - displayHeight) / 2;

    // 3. 用户选择的裁剪区域坐标（基于最终显示的图片，左上角为原点）
    const cropX = completedCrop.x || 0;
    const cropY = completedCrop.y || 0;

    // 4. 处理旋转对坐标的影响
    let adjustedCropX = cropX;
    let adjustedCropY = cropY;
    const adjustedCropWidth = completedCropWidth;
    const adjustedCropHeight = completedCropHeight;

    if (rotation !== 0) {
        // 当图片有旋转时，需要将裁剪区域坐标转换回旋转前的坐标系
        const radians = (rotation * Math.PI) / 180;
        const cos = Math.cos(-radians); // 反向旋转
        const sin = Math.sin(-radians);

        // 计算图片中心点（使用实际的图片尺寸，不是缩放后的尺寸）
        const centerX = displayWidth / 2;
        const centerY = displayHeight / 2;

        // 将裁剪区域中心点转换到旋转前的坐标系
        const cropCenterX = cropX + completedCropWidth / 2;
        const cropCenterY = cropY + completedCropHeight / 2;

        // 相对于图片中心的坐标
        const relativeX = cropCenterX - centerX;
        const relativeY = cropCenterY - centerY;

        // 反向旋转
        const rotatedX = relativeX * cos - relativeY * sin;
        const rotatedY = relativeX * sin + relativeY * cos;

        // 转换回绝对坐标
        adjustedCropX = rotatedX + centerX - completedCropWidth / 2;
        adjustedCropY = rotatedY + centerY - completedCropHeight / 2;
    }

    // 5. 转换为原始图片坐标
    // 缩放后的图片左上角在 (-centerOffsetX, -centerOffsetY)
    // 所以需要加上中心偏移，再除以缩放比例
    const originalX = (adjustedCropX + centerOffsetX) / scaleX;
    const originalY = (adjustedCropY + centerOffsetY) / scaleY;
    const originalWidth = adjustedCropWidth / scaleX;
    const originalHeight = adjustedCropHeight / scaleY;

    // 5. 转换为自然图片坐标
    const offsetX = (originalX / displayWidth) * naturalWidth;
    const offsetY = (originalY / displayHeight) * naturalHeight;
    const finalWidth = (originalWidth / displayWidth) * naturalWidth;
    const finalHeight = (originalHeight / displayHeight) * naturalHeight;

    return {
        scaleX: baseScaleX,
        scaleY: baseScaleY,
        completedCropWidth: finalWidth,
        completedCropHeight: finalHeight,
        offsetX,
        offsetY,
    };
};

/**
 * 应用变换到Canvas并绘制图片
 */
export const drawImageWithTransform = (
    ctx: CanvasRenderingContext2D,
    img: HTMLImageElement,
    params: CropParams,
    canvas: HTMLCanvasElement,
    imageTransform: ImageTransform,
    pixelRatio?: number,
): void => {
    ctx.save();

    // 应用旋转变换（用户看到的图片是旋转后的）
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate(((imageTransform.rotation || 0) * Math.PI) / 180);
    ctx.translate(-canvas.width / 2, -canvas.height / 2);

    const logicalWidth = pixelRatio ? canvas.width / pixelRatio : canvas.width;
    const logicalHeight = pixelRatio ? canvas.height / pixelRatio : canvas.height;

    // 直接使用原始图片的坐标和尺寸（已经在 calculateCropParams 中正确处理了变换）
    ctx.drawImage(
        img,
        params.offsetX, // 原始图片中的 x 坐标
        params.offsetY, // 原始图片中的 y 坐标
        params.completedCropWidth, // 原始图片中的宽度
        params.completedCropHeight, // 原始图片中的高度
        0, // 画布中的目标 x 坐标
        0, // 画布中的目标 y 坐标
        logicalWidth, // 画布中的目标宽度
        logicalHeight, // 画布中的目标高度
    );

    ctx.restore();
};

/**
 * 计算新的变换状态
 */
export const calculateNewTransform = (
    type: 'ROTATE' | 'ZOOM_IN' | 'ZOOM_OUT',
    currentTransform: ImageTransform,
): ImageTransform => {
    const newTransform = { ...currentTransform };

    switch (type) {
        case 'ROTATE': // 旋转
            newTransform.rotation = (newTransform.rotation + 90) % 360;
            break;
        case 'ZOOM_IN': // 放大
            newTransform.scaleX = Number((newTransform.scaleX + 0.1).toFixed(1));
            newTransform.scaleY = Number((newTransform.scaleY + 0.1).toFixed(1));
            break;
        case 'ZOOM_OUT': // 缩小
            newTransform.scaleX = Number((newTransform.scaleX - 0.1).toFixed(1));
            newTransform.scaleY = Number((newTransform.scaleY - 0.1).toFixed(1));
            break;
        default:
            break;
    }

    return newTransform;
};
